import ExcelJS from 'exceljs';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { calculateWorkTime, floorToHalfHour } from '../utils/timeCalc'; 
import { useDayLocks } from "../hooks/useDayLocks";
import { API_URL } from '../config';

const workTypes = [
  '', '日勤', '公休', '有給', '宿直', '宿直明け', '徹夜', '徹夜明け', 
  '出張', '宿泊', '宿泊手当(出張仮払い済)', '休日出勤', '休日出勤/宿泊', 
  '休日出勤/出張', '休日出勤/出張/宿泊', '移動日', '在宅勤務', '夏期休暇', '冬期休暇'
];

const workTypesByDept = {
  'SDC': [
    '', '日勤', '公休', '有給', '宿直', '宿直明け', '徹夜', '徹夜明け', '夏期休暇', '冬期休暇'
  ],
  '制作技術': [
    '', '公休', '有給', '夏期休暇', '冬期休暇'
  ],
  'default': [
    '', '日勤', '公休', '有給', '宿直', '宿直明け', '徹夜', '徹夜明け', 
    '出張', '宿泊', '宿泊手当(出張仮払い済)', '休日出勤', '休日出勤/宿泊', 
    '休日出勤/出張', '休日出勤/出張/宿泊', '移動日', '在宅勤務', '夏期休暇', '冬期休暇'
  ]
};
const weekdays = ['日', '月', '火', '水', '木', '金', '土'];

const BREAK_OPTIONS = [];
for (let i = 0; i <= 5.0; i += 0.5) {
  BREAK_OPTIONS.push(i.toFixed(1));
}

export default function AttendanceTable({ year, month, userId, targetUserId, isAdmin = false, userName, isManager = false }) {
  const [rows, setRows] = useState([]);
  const [currentIsManager, setCurrentIsManager] = useState(isManager);
  const [userDepartment, setUserDepartment] = useState('');
  const navigate = useNavigate();
  const { lockedDays, toggleLock } = useDayLocks(year, month, targetUserId || userId);
  const [saveState, setSaveState] = useState({ saving: false, message: '' });
  const API_BASE = API_URL;

  useEffect(() => {
    const savedUserId = localStorage.getItem("userId");
    if (!savedUserId) navigate("/", { replace: true });
  }, [navigate]);

  const getTotals = (customRows = rows) => {
    const totals = {
      totalDuration: 0, totalOvertime1: 0, totalOvertime2: 0, totalOvertime3: 0,
      totalRealDuration: 0, totalBreakTime: 0, totalHolidayWork: 0,
      countLate: 0, countLeaveEarly: 0, countAbsent: 0,
      countHoliday: 0, countOvernight: 0, countStay: 0, countBusinessTrip: 0,
      totalOvertimeSum: 0, over60Hours: 0,
    };
    if (!Array.isArray(customRows)) return totals;

    customRows.forEach(row => {
      if (!row) return;
      const parseH = (val) => parseFloat(val) || 0;
      totals.totalDuration += parseH(row.duration);
      totals.totalOvertime1 += parseH(row.overtime1);
      totals.totalOvertime2 += parseH(row.overtime2);
      totals.totalOvertime3 += parseH(row.overtime3);
      totals.totalRealDuration += parseH(row.realDuration);
      totals.totalBreakTime += parseH(row.breakTime);
      totals.totalHolidayWork += parseH(row.holidayWorkTime);
      totals.totalOvertimeSum += (parseH(row.overtime1) + parseH(row.overtime2));
      if (row.isLate) totals.countLate += 1;
      if (row.isLeaveEarly) totals.countLeaveEarly += 1;
      if (row.isAbsent) totals.countAbsent += 1;
      if (row.workType === '公休') totals.countHoliday += 1;
      if (row.workType === '宿直') totals.countOvernight += 1;
      
      if (row.workType && row.workType.includes('宿泊') && !row.workType.includes('仮払い済')) totals.countStay += 1;
      
      const isBT = row.isBusinessTrip === true || row.isBusinessTrip === 'true' || row.is_business_trip == 1 || row.is_business_trip === '1';
      const isPrep = row.isPrepaid === true || row.isPrepaid === 'true' || row.is_prepaid == 1 || row.is_prepaid === '1';
      if (isBT && !isPrep) totals.countBusinessTrip += 1;
    });
    if (totals.totalOvertimeSum > 60) totals.over60Hours = totals.totalOvertimeSum - 60;
    return totals;
  };

  const computeDateKey = (row, idx) => {
    const dayNum = Number(row?.date) || (idx + 1);
    const isStartMonth = dayNum >= 11;
    let y = year, m = month;
    if (!isStartMonth) {
      m = month === 12 ? 1 : month + 1;
      y = month === 12 ? year + 1 : year;
    }
    return `${y}-${String(m).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
  };

  const isLocked = (dateKey) => {
    if (lockedDays && Array.isArray(lockedDays)) {
      return lockedDays.includes(dateKey);
    }
    return false;
  };

  const normalizeTimeString = (value) => {
    if (!value) return '';
    let str = value.toString().trim().replace(/[０-９：]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xFEE0));
    if (['~', '〜', '-', 'ー', '～'].includes(str)) return '～';
    if (str.includes(':')) {
      const parts = str.split(':');
      if (parts.length === 2) return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
      return str;
    }
    if (/^\d+$/.test(str)) {
      if (str.length <= 2) return `${str.padStart(2, '0')}:00`;
      if (str.length === 3) return `${str.slice(0, 1).padStart(2, '0')}:${str.slice(1)}`;
      if (str.length >= 4) return `${str.slice(0, -2).padStart(2, '0')}:${str.slice(-2)}`;
    }
    return str;
  };

  const timeToMin = (str) => {
    if (!str || typeof str !== 'string' || !str.includes(':')) return 0;
    const [h, m] = str.split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
  };

  const recalcRow = (row, shouldAutoUpdateBreak = false) => {
    if (row.workType === '徹夜') {
      row.duration = '2.0h'; row.overtime1 = '0.0h'; row.overtime2 = '0.0h'; row.overtime3 = '2.0h'; row.realDuration = '2.0h'; row.holidayWorkTime = '0.0h';
      return row;
    }
    if (row.workType === '徹夜明け') {
      row.duration = '8.0h'; row.overtime1 = '2.0h'; row.overtime2 = '0.0h'; row.overtime3 = '6.0h'; row.realDuration = '8.0h'; row.holidayWorkTime = '0.0h';
      return row;
    }
    const start = row.start || '', end = row.end || '', instruction = row.instruction || '';
    const startMin = timeToMin(start), endMin = timeToMin(end), instructionMin = timeToMin(instruction);
    let calcStartMin = (instruction && startMin < instructionMin) ? instructionMin : startMin;
    let grossDuration = (start && end && endMin > calcStartMin) ? (endMin - calcStartMin) / 60 : 0;
    if (shouldAutoUpdateBreak) row.breakTime = grossDuration >= 6.0 ? '1.0' : '0.0';
    const result = calculateWorkTime(start, end, instruction, row.breakTime);
    const parseVal = (v) => parseFloat(v) || 0;
    let otSum = parseVal(result.overtime), nOt = parseVal(result.nightOvertime), nBonus = parseVal(result.nightBonus);
    let normOt = Math.max(0, otSum - nOt);
    if (currentIsManager) { normOt = 0; nBonus += nOt; nOt = 0; }
    const fmt = (v) => `${v.toFixed(1)}h`;
    row.duration = fmt(floorToHalfHour(grossDuration));
    row.overtime1 = fmt(normOt); row.overtime2 = fmt(nOt); row.overtime3 = fmt(nBonus);
    row.realDuration = fmt(parseVal(result.duration));
    row.holidayWorkTime = (row.workType && row.workType.includes('休日出勤')) ? row.realDuration : '0.0h';
    return row;
  };

  const handleChange = (index, field, value) => {
    const dKey = computeDateKey(rows[index], index);
    if (isLocked(dKey)) return; 
    const updated = [...rows];
    
    if (field === 'workType') {
      updated[index].workType = value;
      if (['夏期休暇', '冬期休暇'].includes(value)) updated[index].note = value;
      if (value === '徹夜') {
        updated[index].instruction = '22:00'; updated[index].start = '22:00'; updated[index].end = '～'; updated[index].breakTime = '0.0';
      } else if (value === '徹夜明け') {
        updated[index].start = '～'; updated[index].end = '32:00'; updated[index].breakTime = '1.0';
      }
      else if (value === '宿直') {
        updated[index].instruction = '17:30'; 
        updated[index].start = '17:30';       
        updated[index].end = '24:00';         
        updated[index].breakTime = '0.0';     
      }
      else if (value === '宿直明け') {
        updated[index].instruction = '05:00'; 
        updated[index].start = '05:00';       
        updated[index].end = '10:30';         
        updated[index].breakTime = '0.0';     
      }
      updated[index] = recalcRow(updated[index], false);
    } else if (['isLate', 'isLeaveEarly', 'isAbsent', 'isBusinessTrip', 'isAccommodation', 'isPrepaid'].includes(field)) {
      updated[index][field] = !!value;
    } else {
      updated[index][field] = value;
    }
    
    if (field === 'breakTime') updated[index] = recalcRow(updated[index], false);
    setRows(updated);
  };

  const handleTimeBlur = (index, field) => {
    const dKey = computeDateKey(rows[index], index);
    if (isLocked(dKey)) return;
    const updated = [...rows];
    updated[index][field] = normalizeTimeString(updated[index][field] ?? '');
    updated[index] = recalcRow(updated[index], ['instruction', 'start', 'end'].includes(field));
    setRows(updated);
  };

  const createEmptyRow = (y, m, d) => {
    const fullDate = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    return {
      date: d, fullDate, day: weekdays[new Date(fullDate).getDay()], instruction: "", start: "", end: "",
      duration: "0.0h", overtime1: "0.0h", overtime2: "0.0h", overtime3: "0.0h", realDuration: "0.0h",
      holidayWorkTime: "0.0h", breakTime: "0.0", note: "", workType: "", 
      isBusinessTrip: false, isAccommodation: false, isPrepaid: false,
      isLate: false, isLeaveEarly: false, isAbsent: false
    };
  };

  const loadAttendance = async (fetchUserId) => {
    try {
      const params = new URLSearchParams({ action: 'load', year, month, user_id: fetchUserId });
      const res = await fetch(`${API_BASE}?${params.toString()}`, { method: 'GET', credentials: 'include' });
      const data = await res.json();
      if (data && data.error === "未ログイン") {
        localStorage.clear(); 
        window.location.href = "/"; 
        return;
      }
      
      if (data && data.is_manager !== undefined) setCurrentIsManager(data.is_manager);
      if (data && data.department !== undefined) setUserDepartment(data.department);

      const rowsList = [];
      let current = new Date(year, month - 1, 11);
      let end = new Date(year, month, 10);
      while (current <= end) {
        rowsList.push(createEmptyRow(current.getFullYear(), current.getMonth() + 1, current.getDate()));
        current.setDate(current.getDate() + 1);
      }
      if (data?.rows?.length > 0) {
        data.rows.forEach(r => {
          const rawDate = r.work_date || '';
          if (!rawDate) return;
          const dbDate = rawDate.substring(0, 10);
          const target = rowsList.find(row => row.fullDate === dbDate);
          if (target) {
            Object.assign(target, {
              instruction: r.instruction ?? '',
              start: (r.work_type === '徹夜明け' && (!r.start_time || r.start_time.startsWith('00:00'))) ? '～' : (r.start_time ?? ''),
              end: (r.work_type === '徹夜' && (!r.end_time || r.end_time.startsWith('00:00'))) ? '～' : (r.end_time ?? ''),
              duration: r.duration ? `${parseFloat(r.duration).toFixed(1)}h` : '0.0h',
              overtime1: r.overtime1 ? `${parseFloat(r.overtime1).toFixed(1)}h` : '0.0h',
              overtime2: r.overtime2 ? `${parseFloat(r.overtime2).toFixed(1)}h` : '0.0h',
              overtime3: r.overtime3 ? `${parseFloat(r.overtime3).toFixed(1)}h` : '0.0h',
              realDuration: r.real_duration ? `${parseFloat(r.real_duration).toFixed(1)}h` : '0.0h',
              holidayWorkTime: r.holiday_work_time ? `${parseFloat(r.holiday_work_time).toFixed(1)}h` : '0.0h',
              breakTime: r.break_time ? parseFloat(r.break_time).toFixed(1) : '0.0',
              note: r.note ?? '', workType: r.work_type ?? '',
              isLate: r.isLate === true || r.isLate == 1, 
              isLeaveEarly: r.isLeaveEarly === true || r.isLeaveEarly == 1, 
              isAbsent: r.isAbsent === true || r.isAbsent == 1,
              isBusinessTrip: r.isBusinessTrip === true || r.is_business_trip == 1,
              isAccommodation: r.isAccommodation === true || r.is_accommodation == 1,
              isPrepaid: r.isPrepaid === true || r.is_prepaid == 1
            });
          }
        });
      }
      setRows(rowsList);
    } catch (e) { console.error('loadAttendance error', e); setRows([]); }
  };

  useEffect(() => {
    const fetchId = targetUserId || userId;
    if (fetchId) loadAttendance(fetchId);
  }, [year, month, targetUserId, userId]);

  const handleSaveOnline = async () => {
    setSaveState({ saving: true, message: '保存中...' });
    try {
      const rowsToSave = rows.map(r => ({ ...r, start: r.start === '～' ? '' : r.start, end: r.end === '～' ? '' : r.end }));
      const res = await fetch(`${API_BASE}?action=save`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: targetUserId || userId, year, month, rows: rowsToSave, lockedDays }),
        credentials: 'include'
      });
      const data = await res.json();

       if (data && data.error === "未ログイン") {
        alert("ログインの有効期限が切れました。再度ログインしてください。"); 
        localStorage.clear();
        window.location.href = "/";
        return false; 
      }

      setSaveState({ saving: false, message: data?.success ? '保存しました' : `保存失敗: ${data?.error}` });
      if (data?.success) await loadAttendance(targetUserId || userId);
      return data?.success;
    } catch (e) { 
      setSaveState({ saving: false, message: '通信エラー' }); 
      return false; 
    }
  };

  const handleLockWithSave = async (dKey, nextLockState) => {
    if (nextLockState === true) {
      await handleSaveOnline(); 
    }
    await toggleLock(dKey, nextLockState);
  };

  const fillWorkbook = (workbook, targetRows, targetUserName, targetIsManagerFlag) => {
    const nonZero = (v) => (v === '0.0h' ? '' : v);
    const formatTimeHHMM = (t) => (!t || t === '～') ? t : t.substring(0, 5);
    const worksheet = workbook.getWorksheet(1);
    worksheet.getCell('AQ3').value = `${year}年${month}月勤務表（${month}月11日～${month === 12 ? 1 : month + 1}月10日）`;
    worksheet.getCell('DJ4').value = targetUserName || '名前不明';

    let excelStayCount = 0;

    for (let i = 0; i < 31; i++) {
      const row = targetRows[i];
      const excelRow = worksheet.getRow(7 + i); 
      if (row && row.date) {
        excelRow.getCell('A').value = row.date;
        excelRow.getCell('J').value = row.day;
        
        const isBT = row.isBusinessTrip === true || row.isBusinessTrip === 'true' || row.isBusinessTrip == 1;
        const isStay = row.isAccommodation === true || row.isAccommodation === 'true' || row.isAccommodation == 1;
        const isPrep = row.isPrepaid === true || row.isPrepaid === 'true' || row.isPrepaid == 1;

        let finalQValue = '';
        if (isPrep) {
          if (isBT && isStay) {
            finalQValue = '出張宿泊仮払済';
          } else if (isStay) {
            finalQValue = '宿泊仮払済';
          } else {
            finalQValue = '出張仮払済';
          }
        } else if (isBT && isStay) {
          finalQValue = '出張宿泊';
        } else if (isStay) {
          finalQValue = '宿泊';
        } else if (isBT) {
          finalQValue = '出張';
        } else {
          finalQValue = row.instruction || '';
        }

        excelRow.getCell('Q').value = finalQValue; 
        excelRow.getCell('HL').value = finalQValue;

        excelRow.getCell('AN').value = formatTimeHHMM(row.start);
        excelRow.getCell('BK').value = formatTimeHHMM(row.end);
        excelRow.getCell('DX').value = nonZero(row.duration);    
        excelRow.getCell('CH').value = nonZero(row.overtime1);   
        excelRow.getCell('CV').value = nonZero(row.overtime2);   
        excelRow.getCell('DJ').value = nonZero(row.overtime3);   
        excelRow.getCell('HM').value = nonZero(row.realDuration);
        excelRow.getCell('EL').value = parseFloat(row.breakTime) || '';
        excelRow.getCell('EZ').value = row.workType === '在宅勤務' ? (row.note ? `在宅勤務 ${row.note}` : '在宅勤務') : row.note;
        excelRow.getCell('HH').value = row.isLate ? '○' : '';
        excelRow.getCell('HI').value = row.isLeaveEarly ? '○' : '';
        excelRow.getCell('HJ').value = row.isAbsent ? '○' : '';

        if (row.workType === '宿直' || isStay || (row.workType && row.workType.includes('宿泊') && !row.workType.includes('仮払い済'))) {
          excelStayCount += 1;
        }
      } else {
        excelRow.getCell('A').value = '';
        excelRow.getCell('J').value = '';
        excelRow.getCell('Q').value = '';
        excelRow.getCell('AN').value = '';
        excelRow.getCell('BK').value = '';
        excelRow.getCell('DX').value = '';
        excelRow.getCell('CH').value = '';
        excelRow.getCell('CV').value = '';
        excelRow.getCell('DJ').value = '';
        excelRow.getCell('HM').value = '';
        excelRow.getCell('EL').value = '';
        excelRow.getCell('EZ').value = '';
        excelRow.getCell('HL').value = '';
        excelRow.getCell('HH').value = '';
        excelRow.getCell('HI').value = '';
        excelRow.getCell('HJ').value = '';
      }
    }

    const t = getTotals(targetRows); 
    worksheet.getCell('BI48').value = t.totalOvertime1;
    worksheet.getCell('DC48').value = t.totalOvertime2;
    worksheet.getCell('EL40').value = t.totalOvertime3;
    worksheet.getCell('EL48').value = t.over60Hours;
    worksheet.getCell('BI55').value = t.totalDuration;
    worksheet.getCell('EL55').value = t.totalRealDuration;
    worksheet.getCell('DC55').value = t.totalBreakTime;
    worksheet.getCell('DU48').value = t.totalHolidayWork;

    worksheet.getCell('GS41').value = t.countBusinessTrip > 0 ? t.countBusinessTrip : '';

    worksheet.getCell('AD51').value = excelStayCount;
    worksheet.getCell('R56').value = excelStayCount * 1000;

    const targetCells = ['E41', 'X41', 'AQ41', 'E43', 'X43', 'AQ43', 'E46', 'X46', 'AQ46'];
    targetRows.filter(row => {
      const isStay = row.isAccommodation === true || row.isAccommodation === 'true' || row.isAccommodation == 1;
      return row.workType === '宿直' || isStay || (row.workType && row.workType.includes('宿泊') && !row.workType.includes('仮払い済'));
    })
    .slice(0, 9).forEach((row, idx) => {
      const p = row.fullDate.split('-');
      if (p.length === 3) {
        worksheet.getCell(targetCells[idx]).value = `${parseInt(p[1])}/${parseInt(p[2])}`;
      }
    });
  };

  const handleExportExcel = async () => {
    const response = await fetch(`/templates/shukkinbo_template.xlsx?v=${new Date().getTime()}`);
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(await response.arrayBuffer());
    fillWorkbook(workbook, rows, userName, currentIsManager);
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `勤務表_${userName}_${year}_${String(month).padStart(2, '0')}.xlsx`);
  };

  const handleBulkExportZip = async () => {
    setSaveState({ saving: true, message: '全社員データ取得中...' });
    try {
      const userRes = await fetch(`${API_BASE}?action=get_users`, { credentials: 'include' });
      const userData = await userRes.json();
      if (!userData.users) throw new Error("ユーザーが取得できませんでした");

      const zip = new JSZip();
      const templateRes = await fetch(`/templates/shukkinbo_template.xlsx?v=${new Date().getTime()}`);
      const templateBuffer = await templateRes.arrayBuffer();

      for (const u of userData.users) {
        setSaveState({ saving: true, message: `${u.name}さんのデータを処理中...` });
        const params = new URLSearchParams({ action: 'load', year, month, user_id: u.id });
        const res = await fetch(`${API_BASE}?${params.toString()}`, { credentials: 'include' });
        const data = await res.json();

        const tempRows = [];
        let cur = new Date(year, month - 1, 11);
        let stop = new Date(year, month, 10);
        while (cur <= stop) {
          tempRows.push(createEmptyRow(cur.getFullYear(), cur.getMonth() + 1, cur.getDate()));
          cur.setDate(cur.getDate() + 1);
        }
        data.rows?.forEach(r => {
          const rawDate = r.work_date || '';
          if (!rawDate) return;
          const target = tempRows.find(row => row.fullDate === rawDate.substring(0, 10));
          if (target) {
            Object.assign(target, {
              instruction: r.instruction || '',
              start: r.start_time || '', end: r.end_time || '',
              duration: `${parseFloat(r.duration || 0).toFixed(1)}h`,
              overtime1: `${parseFloat(r.overtime1 || 0).toFixed(1)}h`,
              overtime2: `${parseFloat(r.overtime2 || 0).toFixed(1)}h`,
              overtime3: `${parseFloat(r.overtime3 || 0).toFixed(1)}h`,
              realDuration: `${parseFloat(r.real_duration || 0).toFixed(1)}h`,
              holidayWorkTime: `${parseFloat(r.holiday_work_time || 0).toFixed(1)}h`,
              breakTime: parseFloat(r.break_time || 0).toFixed(1),
              note: r.note || '', workType: r.work_type || '',
              isLate: r.isLate === true || r.isLate == 1,
              isLeaveEarly: r.isLeaveEarly === true || r.isLeaveEarly == 1,
              isAbsent: r.isAbsent === true || r.isAbsent == 1,
              isBusinessTrip: r.isBusinessTrip === true || r.is_business_trip == 1,
              isAccommodation: r.isAccommodation === true || r.is_accommodation == 1,
              isPrepaid: r.isPrepaid === true || r.is_prepaid == 1
            });
          }
        });

        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(templateBuffer);
        fillWorkbook(workbook, tempRows, u.name, !!u.is_manager);
        const buf = await workbook.xlsx.writeBuffer();
        zip.file(`勤務表_${u.name}_${year}_${month}.xlsx`, buf);
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, `全社員勤務表_${year}_${month}.zip`);
      setSaveState({ saving: false, message: '一括出力完了しました' });
    } catch (e) {
      console.error(e);
      setSaveState({ saving: false, message: '一括出力エラー' });
    }
  };

  return (
    <div className="attendance-table-wrapper" style={{ maxWidth: '100%', height: 'calc(100vh - 60px)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flexShrink: 0, paddingBottom: '5px' }}>
        <h2 style={{ margin: 0 }}>{year}年{month}月勤務表（{month}月11日～{month === 12 ? 1 : month + 1}月10日）</h2>
      </div>
      <div style={{ flexShrink: 0, backgroundColor: '#f0f0f0', border: '1px solid #ccc', padding: '8px', display: 'flex', alignItems: 'center', zIndex: 60, gap: '10px' }}>
          <button onClick={handleSaveOnline} disabled={saveState.saving} style={{ backgroundColor: '#2196F3', color: 'white', border: 'none', padding: '6px 15px', borderRadius: '4px', cursor: 'pointer' }}>
            {saveState.saving ? '処理中...' : '保存'}
          </button>
          <button onClick={handleExportExcel} style={{ padding: '6px 15px', cursor: 'pointer' }}>Excel出力</button>
          
          {isAdmin && (
            <button onClick={handleBulkExportZip} disabled={saveState.saving} style={{ backgroundColor: '#4CAF50', color: 'white', border: 'none', padding: '6px 15px', borderRadius: '4px', cursor: 'pointer' }}>
              全社員一括(ZIP)
            </button>
          )}

          {saveState.message && <span style={{ marginLeft: 12, fontWeight: 'bold', color: '#d32f2f' }}>{saveState.message}</span>}
      </div>
      
      <div className="table-scroll-container" style={{ flexGrow: 1, overflow: 'auto', border: '1px solid #ccc', borderTop: 'none', position: 'relative' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', borderSpacing: 0, whiteSpace: 'nowrap' }}>
          <thead style={{ zIndex: 50 }}>
            <tr>
            <th style={{
              position: 'sticky', top: 0, left: 0,
              width: '60px', minWidth: '60px', whiteSpace: 'nowrap',
              backgroundColor: '#f0f0f0', zIndex: 60,
              boxShadow: 'inset 0 -1px 0 #ccc, inset -1px 0 0 #ccc'
            }}>日付</th>

            <th style={{
              position: 'sticky', top: 0, left: '60px',
              width: '40px', minWidth: '40px',
              backgroundColor: '#f0f0f0', zIndex: 60,
              boxShadow: 'inset 0 -1px 0 #ccc, inset -1px 0 0 #ccc'
            }}>曜日</th>
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#f0f0f0', zIndex: 50, boxShadow: 'inset 0 -1px 0 #ccc' }}>勤務種別</th>
              
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#eef5ff', zIndex: 50, boxShadow: 'inset 0 -1px 0 #ccc', color: '#0056b3' }}>出張</th>
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#eef5ff', zIndex: 50, boxShadow: 'inset 0 -1px 0 #ccc', color: '#0056b3' }}>宿泊</th>
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#eef5ff', zIndex: 50, boxShadow: 'inset 0 -1px 0 #ccc', color: '#0056b3' }}>仮払</th>

              <th style={{ position: 'sticky', top: 0, backgroundColor: '#f0f0f0', zIndex: 50, boxShadow: 'inset 0 -1px 0 #ccc' }}>指示</th>
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#f0f0f0', zIndex: 50, boxShadow: 'inset 0 -1px 0 #ccc' }}>出勤</th>
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#f0f0f0', zIndex: 50, boxShadow: 'inset 0 -1px 0 #ccc' }}>退勤</th>
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#f0f0f0', zIndex: 50, boxShadow: 'inset 0 -1px 0 #ccc' }}>勤務時間</th>
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#f0f0f0', zIndex: 50, boxShadow: 'inset 0 -1px 0 #ccc' }}>残業</th>
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#f0f0f0', zIndex: 50, boxShadow: 'inset 0 -1px 0 #ccc' }}>深夜残業</th>
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#f0f0f0', zIndex: 50, boxShadow: 'inset 0 -1px 0 #ccc' }}>深夜割増</th>
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#f0f0f0', zIndex: 50, boxShadow: 'inset 0 -1px 0 #ccc' }}>実労働時間</th>
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#f0f0f0', zIndex: 50, boxShadow: 'inset 0 -1px 0 #ccc' }}>休憩時間</th>
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#f0f0f0', zIndex: 50, boxShadow: 'inset 0 -1px 0 #ccc' }}>勤務内容</th>
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#f0f0f0', zIndex: 50, boxShadow: 'inset 0 -1px 0 #ccc' }}>遅刻</th>
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#f0f0f0', zIndex: 50, boxShadow: 'inset 0 -1px 0 #ccc' }}>早退</th>
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#f0f0f0', zIndex: 50, boxShadow: 'inset 0 -1px 0 #ccc' }}>欠勤</th>
              {isAdmin && <th style={{ position: 'sticky', top: 0, backgroundColor: '#f0f0f0', zIndex: 50, boxShadow: 'inset 0 -1px 0 #ccc' }}>ロック</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const dKey = computeDateKey(row, idx);
              const locked = isLocked(dKey);
              const rowBgColor = locked ? '#e0e0e0' : '#f4f4f4';

              return (
                <tr key={idx} style={{ backgroundColor: rowBgColor }}>
                  <td style={{
                    position: 'sticky', left: 0,
                    width: '60px', minWidth: '60px', whiteSpace: 'nowrap',
                    backgroundColor: rowBgColor,
                    zIndex: 10,
                    borderBottom: '1px solid #eee',
                    padding: '4px'
                  }}>
                    {row.fullDate ? row.fullDate.substring(5).replace('-', '/') : ''} 
                  </td>

                  <td style={{
                    position: 'sticky', left: '60px',
                    width: '40px', minWidth: '40px',
                    backgroundColor: rowBgColor,
                    zIndex: 10,
                    borderBottom: '1px solid #eee',
                    borderRight: '1px solid #ddd',
                    color: row.day === '日' ? 'red' : row.day === '土' ? 'blue' : 'inherit',
                    textAlign: 'center',
                    padding: '4px'
                  }}>
                    {row.day}
                  </td>
                  
                  <td style={{ borderBottom: '1px solid #eee' }}>
                    <select value={row.workType} onChange={e => handleChange(idx, 'workType', e.target.value)} style={{ width: '95px' }} disabled={locked}>
                      {(workTypesByDept[userDepartment] || workTypesByDept['default']).map((type, i) => (
                        <option key={i} value={type}>{type}</option>
                      ))}
                    </select>
                  </td>
                  
                  <td style={{ borderBottom: '1px solid #eee', textAlign: 'center', backgroundColor: rowBgColor }}>
                    <input type="checkbox" checked={!!row.isBusinessTrip} onChange={e => handleChange(idx, 'isBusinessTrip', e.target.checked)} disabled={locked} />
                  </td>
                  <td style={{ borderBottom: '1px solid #eee', textAlign: 'center', backgroundColor: rowBgColor }}>
                    <input type="checkbox" checked={!!row.isAccommodation} onChange={e => handleChange(idx, 'isAccommodation', e.target.checked)} disabled={locked} />
                  </td>
                  <td style={{ borderBottom: '1px solid #eee', textAlign: 'center', backgroundColor: rowBgColor }}>
                    <input type="checkbox" checked={!!row.isPrepaid} onChange={e => handleChange(idx, 'isPrepaid', e.target.checked)} disabled={locked} />
                  </td>
                  
                  <td style={{ borderBottom: '1px solid #eee' }}><input type="text" value={row.instruction} onChange={e => handleChange(idx, 'instruction', e.target.value)} onBlur={() => handleTimeBlur(idx, 'instruction')} style={{ width: '60px' }} disabled={locked} /></td>
                  <td style={{ borderBottom: '1px solid #eee' }}><input type="text" value={row.start} onChange={e => handleChange(idx, 'start', e.target.value)} onBlur={() => handleTimeBlur(idx, 'start')} style={{ width: '60px' }} disabled={locked} /></td>
                  <td style={{ borderBottom: '1px solid #eee' }}><input type="text" value={row.end} onChange={e => handleChange(idx, 'end', e.target.value)} onBlur={() => handleTimeBlur(idx, 'end')} style={{ width: '60px' }} disabled={locked} /></td>
                  
                  <td style={{ borderBottom: '1px solid #eee' }}><input type="text" value={row.duration} onChange={e => handleChange(idx, 'duration', e.target.value)} disabled={locked} style={{ width: '50px', textAlign: 'center' }} /></td>
                  <td style={{ borderBottom: '1px solid #eee' }}><input type="text" value={row.overtime1} onChange={e => handleChange(idx, 'overtime1', e.target.value)} disabled={locked} style={{ width: '50px', textAlign: 'center', color: '#0056b3' }} /></td>
                  <td style={{ borderBottom: '1px solid #eee', textAlign: 'center', color: '#0056b3' }}><input type="text" value={row.overtime2} onChange={e => handleChange(idx, 'overtime2', e.target.value)} disabled={locked} style={{ width: '50px', textAlign: 'center', color: '#0056b3' }} /></td>
                  <td style={{ borderBottom: '1px solid #eee' }}><input type="text" value={row.overtime3} onChange={e => handleChange(idx, 'overtime3', e.target.value)} disabled={locked} style={{ width: '50px', textAlign: 'center', color: '#0056b3' }} /></td>
                  <td style={{ borderBottom: '1px solid #eee' }}><input type="text" value={row.realDuration} onChange={e => handleChange(idx, 'realDuration', e.target.value)} disabled={locked} style={{ width: '50px', textAlign: 'center' }} /></td>
                  
                  <td style={{ borderBottom: '1px solid #eee' }}>
                    <select value={row.breakTime} style={{ width: '60px' }} onChange={e => handleChange(idx, 'breakTime', e.target.value)} disabled={locked}>
                      {BREAK_OPTIONS.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
                    </select>
                  </td>
                  <td style={{ borderBottom: '1px solid #eee' }}><input value={row.note} style={{ width: '400px' }} onChange={e => handleChange(idx, 'note', e.target.value)} disabled={locked} /></td>
                  <td style={{ borderBottom: '1px solid #eee', textAlign: 'center' }}><input type="checkbox" checked={row.isLate} onChange={e => handleChange(idx, 'isLate', e.target.checked)} disabled={locked} /></td>
                  <td style={{ borderBottom: '1px solid #eee', textAlign: 'center' }}><input type="checkbox" checked={row.isLeaveEarly} onChange={e => handleChange(idx, 'isLeaveEarly', e.target.checked)} disabled={locked} /></td>
                  <td style={{ borderBottom: '1px solid #eee', textAlign: 'center' }}><input type="checkbox" checked={row.isAbsent} onChange={e => handleChange(idx, 'isAbsent', e.target.checked)} disabled={locked} /></td>
                  {isAdmin && (
                    <td style={{ borderBottom: '1px solid #eee', textAlign: 'center' }}>
                      <button 
                        onClick={() => handleLockWithSave(dKey, !locked)}
                        style={{ padding: '2px 8px', fontSize: '11px', backgroundColor: locked ? '#ffc107' : '#e0e0e0', border: '1px solid #999', cursor: 'pointer' }}
                      >
                        {locked ? '🔓 解除' : '🔒 ロック'}
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          {/* ★修正：合計のcolspanを8から9に変更、右側の空きも合わせて調整 */}
          <tfoot>
            {(() => {
              const t = getTotals(rows); 
              return (
                <tr style={{ fontWeight: 'bold', backgroundColor: '#f0f0f0', position: 'sticky', bottom: 0, zIndex: 40 }}>
                  <td colSpan={9} style={{ textAlign: 'right', paddingRight: '10px' }}>合計</td>
                  <td>{t.totalDuration.toFixed(1)}h</td>
                  <td>{t.totalOvertime1.toFixed(1)}h</td>
                  <td>{t.totalOvertime2.toFixed(1)}h</td>
                  <td>{t.totalOvertime3.toFixed(1)}h</td>
                  <td>{t.totalRealDuration.toFixed(1)}h</td>
                  <td>{t.totalBreakTime.toFixed(1)}h</td>
                  <td colSpan={isAdmin ? 5 : 4}></td>
                </tr>
              );
            })()}
          </tfoot>
        </table>
      </div>
    </div>
  );
}